import { ReactElement, ReactNode } from "react";
import styled, { css, CSSObject } from "styled-components";
import type { Strategy } from "@floating-ui/react-dom";

import { useTooltip } from "./tooltip.hooks";
import { TooltipPlacement } from "./tooltip.types";
import { placementDependentStyles } from "./placementDependentStyles";

import { alpha } from "../../typography";

export enum TooltipTestIds {
  Container = "tooltip",
  Tooltip = "tooltip_tooltip",
}

const variants = {
  primary: "var(--color-primary-600)",
  secondary: "var(--color-secondary-600)",
  error: "var(--color-error-600)",
  warning: "var(--color-warning-600)",
} as const;

type StyledTooltipProps = {
  show: boolean;
  customTooltipStyles?: CSSObject;
  variant: keyof typeof variants;
};

const StyledTooltip = styled.div<StyledTooltipProps>`
  --accent-color: ${(props) => variants[props.variant]};

  position: relative;
  z-index: 4000;
  opacity: ${(props) => (props.show ? 1 : 0)};
  transition: opacity ease-in-out 150ms;

  pointer-events: ${(props) => (props.show ? "auto" : "none")};

  ${(props) => css`
    ${props.customTooltipStyles}
  `};
`;

const ReferenceElement = styled.span`
  display: inline-flex;
`;

type TooltipContentProps = {
  placement: TooltipPlacement;
  customContentStyles?: CSSObject;
};

const TooltipContent = styled.div<TooltipContentProps>`
  position: relative;
  background-color: var(--color-neutral-000-full);
  border-radius: var(--size-border-radius-small);
  box-shadow: var(--elevation-4);
  overflow: hidden;
  padding: 12px;
  ${alpha.primaryActive.body3};
  ${(props) => css`
    ${props.customContentStyles}
  `};
  ::after {
    content: "";
    position: absolute;
    ${placementDependentStyles({
      top: css`
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
      `,
      right: css`
        left: 0;
        top: 0;
        bottom: 0;
        width: 2px;
      `,
      bottom: css`
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
      `,
      left: css`
        right: 0;
        top: 0;
        bottom: 0;
        width: 2px;
      `,
    })};

    background-color: var(--accent-color);
  }
`;

const StyledArrow = styled.div`
  position: absolute;
  mix-blend-mode: lighten;

  ::before {
    content: "";
    margin: auto;
    display: block;
    width: 8px;
    height: 8px;
    transform: rotate(45deg);
    background-color: var(--accent-color);
  }
`;

type TooltipProps = {
  /** Unique identifier for the component. */
  id?: string;
  /** Unique identifier for testing the component that overrides the default test id. */
  testId?: string;
  /** ClassName property to style the component. */
  className?: string;
  /** The content that will be rendered inside of the Tooltip. */
  content?: ReactNode;
  /** Where to place the Tooltip relative to the reference element that triggers it. If there is not enough space in the viewport, the Tooltip will move to the opposite side. */
  defaultPlacement?: TooltipPlacement;
  /** Custom tooltip styles */
  customTooltipStyles?: CSSObject;
  /** Custom content styles */
  customContentStyles?: CSSObject;
  /** CSS positioning strategy for the tooltip. */
  strategy?: Strategy;
  /** Tooltip color */
  variant?: keyof typeof variants;
  children: ReactElement;
};

export const Tooltip = ({
  id,
  testId,
  className,
  content,
  defaultPlacement = "top",
  customTooltipStyles,
  customContentStyles,
  strategy,
  variant = "secondary",
  children,
}: TooltipProps) => {
  const {
    arrowStyles,
    tooltipStyles,
    placement,
    arrowRef,
    setReferenceElement,
    setTooltipElement,
    shouldShowTooltip,
    showTooltip,
    hideTooltip,
  } = useTooltip({ defaultPlacement, strategy });

  if (!content) {
    return children;
  }

  return (
    <ReferenceElement
      id={id}
      className={className}
      data-testid={testId ?? TooltipTestIds.Container}
      ref={setReferenceElement}
      onMouseOver={showTooltip}
      onMouseOut={hideTooltip}
    >
      {children}
      <StyledTooltip
        data-testid={testId ? `${testId}_tooltip}` : TooltipTestIds.Tooltip}
        ref={setTooltipElement}
        style={tooltipStyles}
        show={shouldShowTooltip}
        variant={variant}
        customTooltipStyles={customTooltipStyles}
      >
        <TooltipContent
          placement={placement}
          customContentStyles={customContentStyles}
        >
          {content}
        </TooltipContent>
        <StyledArrow ref={arrowRef} style={arrowStyles} />
      </StyledTooltip>
    </ReferenceElement>
  );
};
